import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { http, formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Panel } from "@/components/admin/PageBits";
import { ProgressBar } from "@/components/admin/RedeemProgress";

const cls = "h-9 border-white/10 bg-black/30 text-slate-100";

export const ProgressStagesEditor = () => {
  const { reload } = useBranding();
  const [stages, setStages] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [preview, setPreview] = useState(40);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/settings").then(({ data }) => { setStages(data.progress_stages); setDefaults(data.default_progress_stages); }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (i, k, v) => setStages((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const clean = stages.map((s) => ({ up_to: Math.max(0, Math.min(100, Number(s.up_to) || 0)), label: s.label }));

  const save = async (list = clean) => {
    setBusy(true);
    try {
      const { data } = await http.put("/admin/settings", { progress_stages: list });
      setStages(data.progress_stages);
      reload();
      toast.success("Etapas do progresso salvas.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Panel title="Etapas do progresso do resgate" testId="progress-stages-editor"
      action={<div className="flex gap-2">
        <button onClick={() => save(defaults)} className="btn-ghost-sm" data-testid="stages-reset-btn"><RotateCcw className="h-3.5 w-3.5" /> Restaurar padrão</button>
        <button onClick={() => save()} disabled={busy || clean.some((s) => !s.label.trim())} className="btn-gold h-8 px-3 text-xs" data-testid="stages-save-btn"><Save className="h-3.5 w-3.5" /> Salvar</button>
      </div>}>
      <p className="mb-3 text-xs text-slate-500">Cada frase aparece para o cliente enquanto o progresso estiver até o limite informado. A última etapa deve ir até 100%.</p>
      <div className="space-y-2">
        {stages.map((s, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`stage-row-${i}`}>
            <span className="text-xs text-slate-500">até</span>
            <Input type="number" min={0} max={100} value={s.up_to} onChange={(e) => set(i, "up_to", e.target.value)} className={`${cls} w-20 font-mono-x`} data-testid={`stage-upto-${i}`} />
            <span className="text-xs text-slate-500">%</span>
            <Input value={s.label} maxLength={60} onChange={(e) => set(i, "label", e.target.value)} className={cls} data-testid={`stage-label-${i}`} />
            <button disabled={stages.length === 1} onClick={() => setStages(stages.filter((_, j) => j !== i))} className="icon-btn disabled:opacity-30" data-testid={`stage-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button disabled={stages.length >= 10} onClick={() => setStages([...stages, { up_to: 100, label: "" }])} className="btn-ghost-sm mt-3" data-testid="stage-add-btn"><Plus className="h-3.5 w-3.5" /> Adicionar etapa</button>
      <div className="mt-5 rounded-md border border-white/10 p-4">
        <p className="mb-2 text-xs text-slate-500">Pré-visualização: arraste para simular</p>
        <input type="range" min={0} max={100} value={preview} onChange={(e) => setPreview(Number(e.target.value))} className="mb-3 w-full accent-[var(--brand)]" data-testid="stages-preview-range" />
        <ProgressBar value={preview} stages={clean} testId="stages-preview" />
      </div>
    </Panel>
  );
};
